import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ImportBatchPageDto, ImportService } from '../import.service';
import { ImportBatchDto, ImportBatchStatus, ImportErrorDto, ImportType } from '../models/import.model';

interface ApiImport { id?: string | number; batchId?: string | number; importType?: string; type?: string; fileName?: string; status?: string; totalRows?: number; successRows?: number; errorRows?: number; importedAt?: string; importedBy?: string; creditorId?: string; creditorLabel?: string; errorSummary?: string; errors?: unknown[]; [key: string]: unknown; }
interface ApiPage<T> { content?: T[]; items?: T[]; totalElements?: number; }

@Injectable()
export class ImportApiService extends ImportService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/imports`;

  getBatches(status?: ImportBatchStatus): Observable<ImportBatchPageDto> {
    let params = new HttpParams();
    if (status) params = params.set('status', status);
    return this.http.get<ApiPage<ApiImport> | ApiImport[]>(this.base, { params }).pipe(
      map(response => ({ items: (Array.isArray(response) ? response : (response.content ?? response.items ?? [])).map(mapImport) })),
    );
  }

  getBatch(batchId: string): Observable<ImportBatchDto> {
    return this.http.get<ApiImport>(`${this.base}/${encodeURIComponent(batchId)}`).pipe(map(mapImport));
  }

  override getRejections(batchId: string): Observable<ImportErrorDto[]> {
    return this.http.get<unknown[]>(`${this.base}/${encodeURIComponent(batchId)}/rejections`).pipe(map(rows => rows.map(mapError)));
  }

  override upload(file: File, importType: ImportType): Observable<ImportBatchDto> {
    const body = new FormData();
    body.append('file', file, file.name);
    body.append('importType', importType);
    return this.http.post<ApiImport>(this.base, body).pipe(map(mapImport));
  }
}

export function mapImport(dto: ApiImport): ImportBatchDto {
  return {
    batchId: String(dto.batchId ?? dto.id ?? ''), importType: normalizeType(dto.importType ?? dto.type), fileName: String(dto.fileName ?? ''),
    status: normalizeStatus(dto.status), totalRows: number(dto.totalRows), successRows: number(dto.successRows), errorRows: number(dto.errorRows),
    importedAt: String(dto.importedAt ?? ''), importedBy: String(dto.importedBy ?? ''), creditorId: dto.creditorId, creditorLabel: dto.creditorLabel,
    errorSummary: dto.errorSummary, errors: Array.isArray(dto.errors) ? dto.errors.map(mapError) : undefined,
  };
}

function mapError(value: unknown): ImportErrorDto {
  const dto = (value ?? {}) as Record<string, unknown>;
  return { rowNumber: number(dto['rowNumber'] ?? dto['line']), fieldName: dto['fieldName'] == null ? undefined : String(dto['fieldName']), errorCode: String(dto['errorCode'] ?? dto['code'] ?? ''), errorMessage: String(dto['errorMessage'] ?? dto['message'] ?? ''), rawValue: dto['rawValue'] == null ? undefined : String(dto['rawValue']) };
}
function number(value: unknown): number { return typeof value === 'number' && Number.isFinite(value) ? value : 0; }
function normalizeType(value: unknown): ImportType { const type = String(value ?? 'CSV').toUpperCase(); return (['ERP', 'CLS', 'CSV'] as string[]).includes(type) ? type as ImportType : 'CSV'; }
function normalizeStatus(value: unknown): ImportBatchStatus { const status = String(value ?? 'PENDING').toUpperCase(); return (['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED'] as string[]).includes(status) ? status as ImportBatchStatus : 'PENDING'; }
